import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { CurrentUserService } from '../auth/current-user.service';
import { BusinessLocationContextService } from '../services/business-location-context.service';
import { BusinessService } from '../services/business.service';

export const businessLocationGuard: CanActivateFn = (_route, state) => {
  const businesses = inject(BusinessService);
  const context = inject(BusinessLocationContextService);
  const currentUser = inject(CurrentUserService);
  const router = inject(Router);
  const assignedIds = currentUser.user()?.businessLocationIds ?? [];
  const isOwner = currentUser.isBusinessOwner();

  // Staff locations are assigned and validated by Identity. They do not need
  // LOCATION_VIEW merely to enter a permitted operational module.
  if (!isOwner && assignedIds.length > 0) {
    const selected = context.selectedLocationId();
    if (!selected || !assignedIds.includes(selected)) context.select(assignedIds[0]);
    return true;
  }

  return businesses.stores('business').pipe(
    map((locations) => {
      const active = locations.filter((location) => location.isActive !== false);
      const allowed = isOwner
        ? active
        : active.filter((location) => assignedIds.includes(location.id));

      if (!allowed.length) {
        context.clear();
        return router.createUrlTree(['/business/locations'], {
          queryParams: { required: 'true', returnUrl: state.url },
        });
      }

      const selected = context.selectedLocationId();
      if (!selected || !allowed.some((location) => location.id === selected)) {
        const preferred = allowed.find((location) => location.isPrimary) ?? allowed[0];
        context.select(preferred.id);
      }
      return true;
    }),
    catchError(() => of(router.createUrlTree(['/business/locations'], {
      queryParams: { required: 'true', returnUrl: state.url },
    }))),
  );
};
